import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  createTransportadora,
  getTransportadoraById,
  updateTransportadora,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

const zen = {
  blue: '#1976d2',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper',
  },
};

function Section({ title, children }) {
  return (
    <Box sx={zen.sectionCardSx}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

export default function FormTransportadora({
  transportadoraId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const editing = !!transportadoraId;
  const [form, setForm] = useState({
    cnpj: '',
    razaoSocial: '',
    inscricaoEstadual: '',
    endereco: '',
    cidade: '',
    estado: '',
    ativo: true,
  });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const titulo = useMemo(
    () => (editing ? `Editar transportadora #${transportadoraId}` : 'Nova transportadora'),
    [editing, transportadoraId],
  );

  useEffect(() => {
    (async () => {
      if (!editing) return;
      try {
        const { data } = await getTransportadoraById(transportadoraId);
        setForm({
          cnpj: data.cnpj || '',
          razaoSocial: data.razaoSocial || '',
          inscricaoEstadual: data.inscricaoEstadual || '',
          endereco: data.endereco || '',
          cidade: data.cidade || '',
          estado: data.estado || '',
          ativo: data.ativo !== false,
        });
      } catch (e) {
        setErrorMessage(getApiErrorMessage(e, 'Não foi possível carregar a transportadora.'));
      }
    })();
  }, [editing, transportadoraId]);

  const onChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSaving(true);
    try {
      if (!form.razaoSocial?.trim()) throw new Error('Razão social é obrigatória.');
      const cnpj = onlyDigits(form.cnpj);
      if (cnpj && cnpj.length !== 14) throw new Error('CNPJ inválido.');

      const payload = {
        cnpj: cnpj || null,
        razaoSocial: form.razaoSocial.trim(),
        inscricaoEstadual: form.inscricaoEstadual?.trim() || null,
        endereco: form.endereco?.trim() || null,
        cidade: form.cidade?.trim() || null,
        estado: form.estado?.trim()?.toUpperCase() || null,
        ativo: !!form.ativo,
      };

      if (editing) await updateTransportadora(transportadoraId, payload);
      else await createTransportadora(payload);

      onSaved?.(editing ? 'Transportadora atualizada' : 'Transportadora criada');
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{ p: embedded ? 0 : 2, maxWidth: embedded ? 'none' : 900, mx: 'auto' }}
    >
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          {titulo}
        </Typography>
      )}
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      <Section title="Dados da transportadora">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField label="CNPJ" value={form.cnpj} onChange={onChange('cnpj')} fullWidth />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="Razão social"
              value={form.razaoSocial}
              onChange={onChange('razaoSocial')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Inscrição estadual"
              value={form.inscricaoEstadual}
              onChange={onChange('inscricaoEstadual')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField label="Endereço" value={form.endereco} onChange={onChange('endereco')} fullWidth />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField label="Cidade" value={form.cidade} onChange={onChange('cidade')} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="UF"
              value={form.estado}
              onChange={onChange('estado')}
              fullWidth
              inputProps={{ maxLength: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={form.ativo} onChange={onChange('ativo')} />}
              label="Ativa"
            />
          </Grid>
        </Grid>
      </Section>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button startIcon={<CancelIcon />} onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </Stack>
    </Box>
  );
}
